import React from 'react';
import _ from 'lodash';
import BigNumber from 'bignumber.js';
import moment from 'moment';
import shortid from 'shortid';

import { ProductionGridFieldEnum as ProductionGridFields } from 'Common/FieldEnum';
import {
  ReactTableColumnProperties,
  ReactTableWrapperCogColumnProperties,
  DATE_FORMAT_DAY_OF_WEEK,
} from 'Common/Constants';
import { PopupTextCell, DateCell, DateColumnFilter } from 'Common/GridHelper';
import { ProductionStatusKeyEnum } from 'Common/Constants/Enumeration/ProductionStatusEnum';
import { MenuMealTypeKeysEnum } from 'Common/Constants/Enumeration/MealTypesEnum';
import { GridContextMenu } from 'Components/app/contextmenu/ContextMenuManager';
import {
  ProductionRecordDetailFieldEnum as ProdRecordDetailFields,
  EditProductionRecordFieldEnum as EditProductionFields,
  ProductionRecordFieldEnum as ProdRecordFields,
  RecipeFieldEnum as RecipeFields,
  RecipeIngredientMeasureFieldEnum as RecipeIngMeasureFields,
  ProductionRecipeLeftoverEnum as ProdRecipeLeftoverFields,
  ProductionRecipeLeftoverDetailEnum as ProdLeftoverDetailFields,
} from 'Common/FieldEnum';
import {
  getMealContributionMessage,
  calculateContributionsPerMeasure,
} from 'Pages/managemenu/subpages/editmenu/utilities';
import {
  RecipeCategoryIdMap,
  RecipeCategoryEnum,
} from 'Common/Constants/Enumeration/RecipeCategoryEnum';

import {
  TEMPERATURES,
  TEMPERATURES_FIELD_NAME_MAP,
  OMIT_PROD_DETAIL_SAVE_FIELDS,
  NUMERIC_PROD_DETAIL_SAVE_FIELDS,
  RECIPE_LEFTOVER_FIELDS_TO_LEFTOVER_DETAIL_FIELDS,
} from './Constants';

export const groupDetailsByRecipeCategory = (details, expandedRecipes) => {
  const detailsCopy = _.cloneDeep(details);
  const [ingredients, recipes] = _.partition(detailsCopy, (pd) =>
    Boolean(
      pd[ProdRecordDetailFields.IngredientId] &&
        !pd[ProdRecordDetailFields.IsSingleIngredientRecipe]
    )
  );

  const recipesWithIng = recipes.map((r) => {
    return {
      ...r,
      [ProdRecordDetailFields.Ingredients]: ingredients.filter(
        (i) =>
          i[ProdRecordDetailFields.RecipeId] ===
            r[ProdRecordDetailFields.RecipeId] &&
          i[ProdRecordDetailFields.IsLeftover] ===
            r[ProdRecordDetailFields.IsLeftover]
      ),
    };
  });

  const recipesByCategory = _.groupBy(
    recipesWithIng,
    ProdRecordDetailFields.RecipeCategoryId
  );

  Object.keys(recipesByCategory).forEach((categoryId) => {
    recipesByCategory[categoryId] = getDetailsForCategory(
      recipesByCategory[categoryId],
      expandedRecipes
    );
  });

  return recipesByCategory;
};

export const getContributionsMessage = (detail) => {
  const contributionsPerMeasure = {
    Fruit: detail[ProdRecordDetailFields.Fruit],
    DarkGreenVeg: detail[ProdRecordDetailFields.DarkGreenVeg],
    RedOrangeVeg: detail[ProdRecordDetailFields.RedOrangeVeg],
    LegumesVeg: detail[ProdRecordDetailFields.LegumesVeg],
    StarchyVeg: detail[ProdRecordDetailFields.StarchyVeg],
    OtherVeg: detail[ProdRecordDetailFields.OtherVeg],
    Grain: detail[ProdRecordDetailFields.Grain],
    MeatAlt: detail[ProdRecordDetailFields.MeatAlt],
    Milk: detail[ProdRecordDetailFields.Milk],
  };

  return getMealContributionMessage({
    ContributionsPerMeasure: contributionsPerMeasure,
    QtyOfPortion: detail[ProdRecordDetailFields.QtyOfPortion],
  });
};

export const getRecipeCategoryLabel = (id) => {
  if (!RecipeCategoryIdMap[id]) {
    return 'Other';
  }

  if (id === RecipeCategoryEnum.Meat_MeatAlt) {
    return RecipeCategoryIdMap[RecipeCategoryEnum.Entree];
  }
  return RecipeCategoryIdMap[id];
};

export const computePlannedTotal = (detail) => {
  if (!detail) {
    return 0;
  }
  const plannedReimb = Number(detail[ProdRecordDetailFields.ReimbursableMeals]) || 0;
  const plannedAlacarte = Number(detail[ProdRecordDetailFields.PlannedAlacarte]) || 0;
  const plannedAdult = Number(detail[ProdRecordDetailFields.PlannedAdult]) || 0;
  const plannedPreK = Number(detail[ProdRecordDetailFields.PlannedPreK]) || 0;

  return plannedReimb + plannedAlacarte + plannedAdult + plannedPreK;
};

export const computeOfferedTotal = (detail) => {
  const offered = Number(detail[ProdRecordDetailFields.Offered]) || 0;
  const offeredAdult = Number(detail[ProdRecordDetailFields.OfferedAdult]) || 0;
  const offeredAlacarte = Number(detail[ProdRecordDetailFields.OfferedAlacarte]) || 0;
  const offeredPreK = Number(detail[ProdRecordDetailFields.OfferedPreK]) || 0;

  return offered + offeredAdult + offeredAlacarte + offeredPreK;
};

export const computeReimbServedTotal = (detail) => {
  const served = Number(detail[ProdRecordDetailFields.Served]) || 0;
  const servedAlacarte = Number(detail[ProdRecordDetailFields.ServedAlacarte]) || 0;
  const servedAdult = Number(detail[ProdRecordDetailFields.ServedAdult]) || 0;
  const leftover = Number(detail[ProdRecordDetailFields.Leftover]) || 0;
  const discard = Number(detail[ProdRecordDetailFields.Discard]) || 0;
  const preK = Number(detail[ProdRecordDetailFields.ServedPreK]) || 0;
  
  const totalReimbursableServed = served - servedAlacarte - servedAdult - leftover - discard - preK;  

  return totalReimbursableServed >= 0 ? totalReimbursableServed : 0;
};

export const getTemperaturesInUse = (prodRecords) => {
  const temperaturesInUse = {};

  TEMPERATURES.forEach((temp) => {
    const field = temp.FieldName;

    temperaturesInUse[field] = prodRecords.some((prodRecord) => {
      const { Details } = prodRecord;

      if (Details) {
        return Details.some((detail) => Boolean(detail[field]));
      }
      return false;
    });
  });

  return temperaturesInUse;
};

export const getTemperaturesList = (temperaturesInUse) => {
  let temperatures = [];

  Object.keys(temperaturesInUse).forEach((k) => {
    if (temperaturesInUse[k] && TEMPERATURES_FIELD_NAME_MAP[k]) {
      const temperature = TEMPERATURES_FIELD_NAME_MAP[k];
      temperatures.push(temperature.Description);
    }
  });

  if (temperatures.length < 1) {
    temperatures = [''];
  }

  return temperatures;
};

export const convertIngsToProdRecordDetails = ({
  ings,
  recipe,
  isLeftover = false,
}) => {
  const ingDetails = ings.map((ing) => {
    const ingDetail = {
      [ProdRecordDetailFields.AvailableReserve]: false,
      [ProdRecordDetailFields.Comments]: '',
      [ProdRecordDetailFields.Fruit]: 0,
      [ProdRecordDetailFields.DarkGreenVeg]: 0,
      [ProdRecordDetailFields.RedOrangeVeg]: 0,
      [ProdRecordDetailFields.LegumesVeg]: 0,
      [ProdRecordDetailFields.StarchyVeg]: 0,
      [ProdRecordDetailFields.OtherVeg]: 0,
      [ProdRecordDetailFields.Grain]: 0,
      [ProdRecordDetailFields.MeatAlt]: 0,
      [ProdRecordDetailFields.Milk]: 0,
      [ProdRecordDetailFields.Name]: ing[RecipeIngMeasureFields.IngredientName],
      [ProdRecordDetailFields.HoldTemperature]: null,
      [ProdRecordDetailFields.IngredientId]:
        ing[RecipeIngMeasureFields.IngredientId],
      [ProdRecordDetailFields.Kitchen]: false,
      [ProdRecordDetailFields.Leftover]: 0,
      [ProdRecordDetailFields.LeftoverTemperature]: null,
      [ProdRecordDetailFields.MeasureOfPortion]: '',
      [ProdRecordDetailFields.Offered]: 0,
      [ProdRecordDetailFields.OfferedAdult]: 0,
      [ProdRecordDetailFields.OfferedAlacarte]: 0,
      [ProdRecordDetailFields.PlannedAdult]: 0,
      [ProdRecordDetailFields.PlannedAlacarte]: 0,
      [ProdRecordDetailFields.PreparedTemperature]: null,
      [ProdRecordDetailFields.QtyOfPortion]: recipe[RecipeFields.QtyOfPortion],
      [ProdRecordDetailFields.RecipeCategoryId]:
        recipe[RecipeFields.RecipeCategoryId],
      [ProdRecordDetailFields.RecipeId]: recipe[RecipeFields.Id],
      [ProdRecordDetailFields.ReimbursableMeals]: 0,
      [ProdRecordDetailFields.RemovedTemperature]: null,
      [ProdRecordDetailFields.Served]: 0,
      [ProdRecordDetailFields.ServedAlacarte]: 0,
      [ProdRecordDetailFields.ServedAdult]: 0,
      [ProdRecordDetailFields.TargetTemperature]: null,
      [ProdRecordDetailFields.TimeFirstPanPrepared]: null,
      [ProdRecordDetailFields.x3History]: 0,

      [ProdRecordDetailFields.IsSingleIngredientRecipe]: false,
      [ProdRecordDetailFields.IngredientMeasure1]:
        ing[RecipeIngMeasureFields.Measure1],
      [ProdRecordDetailFields.IngredientQuantity1]:
        ing[RecipeIngMeasureFields.Quantity1] || 0,
      [ProdRecordDetailFields.IngredientMeasure2]:
        ing[RecipeIngMeasureFields.Measure2],
      [ProdRecordDetailFields.IngredientQuantity2]:
        ing[RecipeIngMeasureFields.Quantity2] || 0,
      [ProdRecordDetailFields.IsLeftover]: isLeftover,
      [ProdRecordDetailFields.ReactKey]: shortid.generate(),
    };

    ingDetail[ProdRecordDetailFields.IngredientAmount] = getIngredientAmount(
      ingDetail,
      recipe
    );

    return ingDetail;
  });

  return ingDetails;
};

export const convertRecipeForProdRecord = ({ recipe, isLeftover = false }) => {
  const contributions = calculateContributionsPerMeasure(recipe);
  const ingMeasures = recipe[RecipeFields.Measures] || [];
  const isSingleIngredientRecipe = ingMeasures.length <= 1;

  const prodRecordRecipe = {
    [ProdRecordDetailFields.AvailableReserve]: false,
    [ProdRecordDetailFields.Comments]: '',
    [ProdRecordDetailFields.Fruit]: contributions.Fruit,
    [ProdRecordDetailFields.DarkGreenVeg]: contributions.DarkGreenVeg,
    [ProdRecordDetailFields.RedOrangeVeg]: contributions.RedOrangeVeg,
    [ProdRecordDetailFields.LegumesVeg]: contributions.LegumesVeg,
    [ProdRecordDetailFields.StarchyVeg]: contributions.StarchyVeg,
    [ProdRecordDetailFields.OtherVeg]: contributions.OtherVeg,
    [ProdRecordDetailFields.Grain]: contributions.TotalGrain,
    [ProdRecordDetailFields.MeatAlt]: contributions.MeatAlt,
    [ProdRecordDetailFields.Milk]: contributions.Milk,
    [ProdRecordDetailFields.Name]: recipe[RecipeFields.Name],
    [ProdRecordDetailFields.HoldTemperature]: null,
    [ProdRecordDetailFields.IngredientId]: null,
    [ProdRecordDetailFields.Kitchen]: false,
    [ProdRecordDetailFields.Leftover]: 0,
    [ProdRecordDetailFields.LeftoverTemperature]: null,
    [ProdRecordDetailFields.MeasureOfPortion]:
      recipe[RecipeFields.MeasureOfPortion],
    [ProdRecordDetailFields.Offered]: 0,
    [ProdRecordDetailFields.OfferedAdult]: 0,
    [ProdRecordDetailFields.OfferedAlacarte]: 0,
    [ProdRecordDetailFields.PlannedAdult]: 0,
    [ProdRecordDetailFields.PlannedAlacarte]: 0,
    [ProdRecordDetailFields.PreparedTemperature]: null,
    [ProdRecordDetailFields.QtyOfPortion]: recipe[RecipeFields.QtyOfPortion],
    [ProdRecordDetailFields.RecipeCategoryId]:
      recipe[RecipeFields.RecipeCategoryId],
    [ProdRecordDetailFields.RecipeId]: recipe[RecipeFields.Id],
    [ProdRecordDetailFields.ReimbursableMeals]: 0,
    [ProdRecordDetailFields.RemovedTemperature]: null,
    [ProdRecordDetailFields.Served]: 0,
    [ProdRecordDetailFields.ServedAlacarte]: 0,
    [ProdRecordDetailFields.ServedAdult]: 0,
    [ProdRecordDetailFields.TargetTemperature]: null,
    [ProdRecordDetailFields.TimeFirstPanPrepared]: null,
    [ProdRecordDetailFields.x3History]: 0,
    [ProdRecordDetailFields.IsSingleIngredientRecipe]: isSingleIngredientRecipe,
    [ProdRecordDetailFields.IngredientAmount]: '',
    [ProdRecordDetailFields.IsLeftover]: false,
    [ProdRecordDetailFields.PlannedPercentage]: '0.00',
    [ProdRecordDetailFields.Discard]: 0,
    [ProdRecordDetailFields.RecipeNumberOfPortion]:
      recipe[RecipeFields.NumberOfPortion],
    [ProdRecordDetailFields.ReactKey]: shortid.generate(),
  };

  if (isLeftover) {
    const portionsList = recipe[ProdRecipeLeftoverFields.Leftovers];
    const totalPortions = getTotalLeftovers(portionsList);
    prodRecordRecipe[ProdRecordDetailFields.Leftovers] = portionsList;
    prodRecordRecipe[ProdRecordDetailFields.IsLeftover] = true;
    prodRecordRecipe[ProdRecordDetailFields.Offered] = totalPortions;
    prodRecordRecipe[ProdRecordDetailFields.Served] = totalPortions;
    prodRecordRecipe[ProdRecordDetailFields.PlannedPercentage] = '0.00';
  }

  if (isSingleIngredientRecipe) {
    const ingMeasure = ingMeasures[0];

    if (ingMeasure) {
      prodRecordRecipe[ProdRecordDetailFields.IngredientId] =
        ingMeasure[RecipeIngMeasureFields.IngredientId];
      prodRecordRecipe[ProdRecordDetailFields.IngredientQuantity1] =
        ingMeasure[RecipeIngMeasureFields.Quantity1] || 0;
      prodRecordRecipe[ProdRecordDetailFields.IngredientMeasure1] =
        ingMeasure[RecipeIngMeasureFields.Measure1];
      prodRecordRecipe[ProdRecordDetailFields.IngredientQuantity2] =
        ingMeasure[RecipeIngMeasureFields.Quantity2] || 0;
      prodRecordRecipe[ProdRecordDetailFields.IngredientMeasure2] =
        ingMeasure[RecipeIngMeasureFields.Measure2];
      prodRecordRecipe[ProdRecordDetailFields.IngredientId] =
        prodRecordRecipe[ProdRecordDetailFields.IngredientId];

      prodRecordRecipe[
        ProdRecordDetailFields.IngredientAmount
      ] = getIngredientAmount(prodRecordRecipe, recipe);
    }
  }

  const ingDetails = convertIngsToProdRecordDetails({
    ings: ingMeasures,
    recipe,
    isLeftover,
  });

  return isSingleIngredientRecipe
    ? [prodRecordRecipe]
    : [prodRecordRecipe, ...ingDetails];
};

/**
 * Determines if the production detail is an ingredient. Single ingredient recipes will not be treated
 * as having ingredients.
 * @param {Object} detail
 */
export const isDetailIngredient = (detail = {}) => {
  return (
    Boolean(detail[ProdRecordDetailFields.IngredientId]) &&
    !detail[ProdRecordDetailFields.IsSingleIngredientRecipe]
  );
};

export const isLeftover = (detail = {}) => {
  return detail[ProdRecordDetailFields.IsLeftover];
};

export const shouldShowIngredients = (detail = {}, expandedRecipes = {}) => {
  const toggleId = createToggleId(
    detail[ProdRecordDetailFields.RecipeId],
    detail[ProdRecordDetailFields.IsLeftover]
  );

  return expandedRecipes[toggleId];
};

export const getDetailsForCategory = (details = {}, expandedRecipes = {}) => {
  let items = [];

  details.forEach((d) => {
    items.push(d);

    if (shouldShowIngredients(d, expandedRecipes)) {
      items = items.concat(d[ProdRecordDetailFields.Ingredients]);
    }
  });

  return items;
};

export const computeColumnTotal = (details, fieldName) => {
  const value = _.sumBy(details, (d) => {
    if (isDetailIngredient(d)) {
      return 0;
    }
    return Number(d[fieldName]) || 0;
  });

  return value;
};

/**
 *
 */
export const getIngredientAmount = (detail, recipe) => {
  const plannedTotal = computePlannedTotal(recipe);

  const rQtyOfPortion = Number(
    _.get(recipe, ProdRecordDetailFields.RecipeQtyOfPortion, 0)
  );
  const qtyOfPortion = Number(
    _.get(recipe, ProdRecordDetailFields.QtyOfPortion, 0)
  );

  const rNumberOfPortion = Number(
    _.get(recipe, ProdRecordDetailFields.RecipeNumberOfPortion),
    0
  );

  let ingAmount = '';

  if (!plannedTotal || !rQtyOfPortion || !qtyOfPortion || !rNumberOfPortion) {
    return '';
  }

  const portionRatio = plannedTotal / rNumberOfPortion;
  const qtyOfPortionRatio = qtyOfPortion / rQtyOfPortion;
  const ratio = portionRatio * qtyOfPortionRatio;

  const qty1 = Number(
    _.get(detail, ProdRecordDetailFields.IngredientQuantity1, 0)
  );
  const qty2 = Number(
    _.get(detail, ProdRecordDetailFields.IngredientQuantity2, 0)
  );

  const amount1 = qty1 * ratio;
  const amount2 = qty2 * ratio;

  if (amount1) {
    const measure1 = detail[ProdRecordDetailFields.IngredientMeasure1];
    ingAmount = `${amount1.toFixed(2)} ${measure1}`;
  }

  if (amount2) {
    const measure2 = detail[ProdRecordDetailFields.IngredientMeasure2];
    const amount2Str = `${amount2.toFixed(2)} ${measure2}`;

    ingAmount = ingAmount ? `${ingAmount} ${amount2Str}` : amount2Str;
  }

  return ingAmount;
};

export const getRecipeIngredients = (details, recipeId) => {
  const ingredients = details.filter(
    (d) =>
      isDetailIngredient(d) && recipeId === d[ProdRecordDetailFields.RecipeId]
  );

  return ingredients;
};

export const getRecipeFromDetails = (details, recipeId) => {
  const recipe = details.find(
    (d) =>
      recipeId === d[ProdRecordDetailFields.RecipeId] &&
      (!isDetailIngredient(d) ||
        d[ProdRecordDetailFields.IsSingleIngredientRecipe])
  );

  return recipe;
};

export const getProductionRecordFieldName = (index, field) => {
  return `${EditProductionFields.ProductionRecords}.${index}.${field}`;
};

export const addUIFieldsToDetails = (prodRecord) => {
  const details = prodRecord[ProdRecordFields.Details];
  const feedingFigure = prodRecord[ProdRecordFields.FeedingFigure];

  const detailsWithPlannedPercentages = details.map((d, i) => {
    const plannedReimb =
      Number(d[ProdRecordDetailFields.ReimbursableMeals]) || 0;
    let plannedPercentage;

    if (feedingFigure) {
      plannedPercentage = new BigNumber(plannedReimb)
        .dividedBy(feedingFigure)
        .multipliedBy(100)
        .toFixed(2);
    } else {
      plannedPercentage = '0.00';
    }

    return {
      ...d,
      [ProdRecordDetailFields.PlannedPercentage]: plannedPercentage,
      [ProdRecordDetailFields.ReactKey]: shortid.generate(),
      [ProdRecordDetailFields.ArrayIndex]: i,
    };
  });

  return {
    ...prodRecord,
    [ProdRecordFields.Details]: detailsWithPlannedPercentages,
  };
};

export const convertProdRecordForSave = (prodRecord) => {
  const copy = _.cloneDeep(prodRecord);

  const details = copy[ProdRecordFields.Details].map((d) =>
    _.omit(d, OMIT_PROD_DETAIL_SAVE_FIELDS)
  );

  details.forEach((d) => {
    NUMERIC_PROD_DETAIL_SAVE_FIELDS.forEach((field) => {
      const hasValue = Boolean(d[field]);

      if (!hasValue) {
        d[field] = 0;
      }
    });
  });

  copy[ProdRecordFields.Details] = details;

  return copy;
};

export const computeFeedingFigureTotal = (prodRecord) => {
  const ff = Number(prodRecord[ProdRecordFields.FeedingFigure]) || 0;
  const ffAlacarte =
    Number(prodRecord[ProdRecordFields.FeedingFigureAlacarte]) || 0;
  const ffAdult = Number(prodRecord[ProdRecordFields.FeedingFigureAdult]) || 0;

  return ff + ffAlacarte + ffAdult;
};

export const isEntree = (recipeCategoryId) => {
  return Number(recipeCategoryId) === RecipeCategoryEnum.Entree;
};

export const isMilk = (recipeCategoryId) => {
  return Number(recipeCategoryId) === RecipeCategoryEnum.Milk;
};

export const shouldShowRedPercentage = (recipeCategoryId, percentage) => {
  if (isEntree(recipeCategoryId) || isMilk(recipeCategoryId)) {
    return !(Number(percentage) === 100);
  }
  return false;
};

export const condenseRecipeLeftovers = (recipeLeftovers) => {
  const leftovers = {};

  recipeLeftovers.forEach((rl, i) => {
    const recipeId = rl[ProdRecipeLeftoverFields.Id];

    if (!leftovers[recipeId]) {
      leftovers[recipeId] = _.omit(rl, [
        ProdRecipeLeftoverFields.UseByDT,
        ProdRecipeLeftoverFields.Portions,
        ProdRecipeLeftoverFields.RecipeLeftoverId,
        ProdRecipeLeftoverFields.SiteId,
        ProdRecipeLeftoverFields.RecipeId,
      ]);

      leftovers[recipeId][ProdRecipeLeftoverFields.Leftovers] = [
        {
          ..._.pick(rl, RECIPE_LEFTOVER_FIELDS_TO_LEFTOVER_DETAIL_FIELDS),
          [ProdLeftoverDetailFields.RecipeId]: recipeId,
        },
      ];
    } else {
      const existingRecipe = leftovers[recipeId];

      if (Array.isArray(existingRecipe[ProdRecipeLeftoverFields.Leftovers])) {
        existingRecipe[ProdRecipeLeftoverFields.Leftovers].push({
          ..._.pick(rl, RECIPE_LEFTOVER_FIELDS_TO_LEFTOVER_DETAIL_FIELDS),
          [ProdLeftoverDetailFields.RecipeId]: recipeId,
        });
      }
    }
  });

  return Object.values(leftovers);
};

export const getTotalLeftovers = (leftoversList) => {
  return _.sumBy(
    leftoversList,
    (l) => Number(l[ProdRecipeLeftoverFields.Portions]) || 0
  );
};

/**
 * Determines the maximum amount allowed for a given leftover based on
 * what is currently in use and what is left.
 * @param {Object} leftoverDetail the production record detail
 * @param {Object} leftoverRecipe the available leftover (same as the one from the leftover panel)
 */
export const getMaxAllowedForLeftover = (leftoverDetail, leftoverRecipe) => {
  const detailLeftovers = leftoverDetail[ProdRecordDetailFields.Leftovers];
  const recipeId = leftoverDetail[ProdRecordDetailFields.RecipeId];
  const availableLeftovers = leftoverRecipe[ProdRecipeLeftoverFields.Leftovers];
  const filteredLeftovers = availableLeftovers.filter(
    (l) => l[ProdLeftoverDetailFields.RecipeId] === recipeId
  );

  const currentAmount = getTotalLeftovers(detailLeftovers);
  const availableAmount = getTotalLeftovers(filteredLeftovers);

  return currentAmount + availableAmount;
};

/**
 * Returns two lists, one list contains the changes that will be made to the leftovers on the production record detail.
 * The other list returns the changes that will be made to the recipe leftovers.
 * @param {Number} amount - the new leftover amount
 * @param {Object} leftoverDetail - the production record detail
 * @param {Object} leftoverRecipe - the recipe leftover
 */
export const getLeftoverChanges = ({
  amount,
  leftoverDetail,
  leftoverRecipe,
}) => {
  const detailLeftovers = leftoverDetail[ProdRecordDetailFields.Leftovers];
  const currentAmount = getTotalLeftovers(detailLeftovers);
  const maxAmount = getMaxAllowedForLeftover(leftoverDetail, leftoverRecipe);
  const newAmount = Number(amount) || 0;

  const detailChanges = [];
  const leftoverRecipeChanges = [];

  if (newAmount >= currentAmount) {
    let amountToAdd =
      newAmount > maxAmount
        ? maxAmount - currentAmount
        : newAmount - currentAmount;

    const availableLeftovers = [
      ...leftoverRecipe[ProdRecipeLeftoverFields.Leftovers],
    ];
    availableLeftovers.sort(
      (lo1, lo2) =>
        moment(lo1[ProdLeftoverDetailFields.UseByDT]).valueOf() -
        moment(lo2[ProdLeftoverDetailFields.UseByDT]).valueOf()
    );

    for (const leftover of availableLeftovers) {
      if (amountToAdd === 0) {
        break;
      }
      const availableAmount =
        Number(leftover[ProdLeftoverDetailFields.Portions]) || 0;

      if (availableAmount >= amountToAdd) {
        detailChanges.push({
          [ProdLeftoverDetailFields.RecipeLeftoverId]:
            leftover[ProdLeftoverDetailFields.RecipeLeftoverId],
          [ProdLeftoverDetailFields.Portions]: amountToAdd,
          [ProdLeftoverDetailFields.RecipeId]:
            leftover[ProdLeftoverDetailFields.RecipeId],
          [ProdLeftoverDetailFields.UseByDT]:
            leftover[ProdLeftoverDetailFields.UseByDT],
        });
        leftoverRecipeChanges.push({
          [ProdLeftoverDetailFields.RecipeLeftoverId]:
            leftover[ProdLeftoverDetailFields.RecipeLeftoverId],
          [ProdLeftoverDetailFields.Portions]: -amountToAdd,
        });
        amountToAdd = 0;
      } else {
        detailChanges.push({
          [ProdLeftoverDetailFields.RecipeLeftoverId]:
            leftover[ProdLeftoverDetailFields.RecipeLeftoverId],
          [ProdLeftoverDetailFields.Portions]: availableAmount,
          [ProdLeftoverDetailFields.RecipeId]:
            leftover[ProdLeftoverDetailFields.RecipeId],
          [ProdLeftoverDetailFields.UseByDT]:
            leftover[ProdLeftoverDetailFields.UseByDT],
        });
        leftoverRecipeChanges.push({
          [ProdLeftoverDetailFields.RecipeLeftoverId]:
            leftover[ProdLeftoverDetailFields.RecipeLeftoverId],
          [ProdLeftoverDetailFields.Portions]: -availableAmount,
        });
        amountToAdd -= availableAmount;
      }
    }
  } else if (newAmount < currentAmount) {
    let amountToRemove = currentAmount - newAmount;
    const detailLeftoversCopy = [...detailLeftovers];
    detailLeftoversCopy.sort(
      (lo1, lo2) =>
        moment(lo2[ProdLeftoverDetailFields.UseByDT]).valueOf() -
        moment(lo1[ProdLeftoverDetailFields.UseByDT]).valueOf()
    );

    for (const leftover of detailLeftoversCopy) {
      if (amountToRemove === 0) {
        break;
      }
      const availableAmount =
        Number(leftover[ProdLeftoverDetailFields.Portions]) || 0;
      const recipeLeftoverId =
        leftover[ProdLeftoverDetailFields.RecipeLeftoverId];

      if (amountToRemove <= availableAmount) {
        detailChanges.push({
          [ProdLeftoverDetailFields.RecipeLeftoverId]: recipeLeftoverId,
          [ProdLeftoverDetailFields.Portions]: -amountToRemove,
          [ProdLeftoverDetailFields.RecipeId]:
            leftover[ProdLeftoverDetailFields.RecipeId],
          [ProdLeftoverDetailFields.UseByDT]:
            leftover[ProdLeftoverDetailFields.UseByDT],
        });
        leftoverRecipeChanges.push({
          [ProdLeftoverDetailFields.RecipeLeftoverId]: recipeLeftoverId,
          [ProdLeftoverDetailFields.Portions]: amountToRemove,
        });
        amountToRemove = 0;
      } else {
        detailChanges.push({
          [ProdLeftoverDetailFields.RecipeLeftoverId]: recipeLeftoverId,
          [ProdLeftoverDetailFields.Portions]: -availableAmount,
          [ProdLeftoverDetailFields.RecipeId]:
            leftover[ProdLeftoverDetailFields.RecipeId],
          [ProdLeftoverDetailFields.UseByDT]:
            leftover[ProdLeftoverDetailFields.UseByDT],
        });
        leftoverRecipeChanges.push({
          [ProdLeftoverDetailFields.RecipeLeftoverId]: recipeLeftoverId,
          [ProdLeftoverDetailFields.Portions]: availableAmount,
        });
        amountToRemove -= availableAmount;
      }
    }
  }

  return {
    detailChanges,
    leftoverRecipeChanges,
  };
};

export const getNewLeftoverAmount = ({ detail, fieldName, value }) => {
  const detailCopy = { ...detail };
  detailCopy[fieldName] = value;

  if (isLeftover(detail)) {
    switch (fieldName) {
      case ProdRecordDetailFields.Offered:
      case ProdRecordDetailFields.OfferedAlacarte:
      case ProdRecordDetailFields.OfferedAdult:
        return computeOfferedTotal(detailCopy);
      case ProdRecordDetailFields.Served:
        return value;
      default:
        return 0;
    }
  }

  return 0;
};

export const createToggleId = (recipeId, isLeftover = false) => {
  return isLeftover ? `(L) ${recipeId}` : recipeId;
};

/**
 * Converts Production Record status to readable text
 * @param {Integer} status
 * @example
 * convertStatusToText(0) // planning
 * convertStatusToText(1) // open
 * convertStatusToText(2) // closed
 */
export const convertStatusToText = (status) => {
  return _.toLower(_.get(ProductionStatusKeyEnum, status, ''));
};

export const createProdRecordTableColumns = ({
  sitesMap,
  showCog = false,
  menuOptions,
}) => {
  const columns = [
    {
      Header: 'Menu Title',
      accessor: ProductionGridFields.CycleMenuDescription,
      Cell: (row) => <PopupTextCell text={row.value} />,
      ...ReactTableColumnProperties,
      width: 150,
    },
    {
      Header: 'Site Id',
      accessor: ProductionGridFields.SiteId,
      Cell: (row) => <PopupTextCell text={row.value} />,
      ...ReactTableColumnProperties,
    },
    {
      id: ProductionGridFields.Site,
      Header: 'Site',
      accessor: (row) => {
        const site = sitesMap[row[ProductionGridFields.SiteId]];

        return site ? site.Description : '';
      },
      Cell: (row) => <PopupTextCell text={row.value} />,
      ...ReactTableColumnProperties,
    },
    {
      id: 'ServingDay',
      Header: 'Serving Day',
      accessor: (row) => {
        const serveDate = row[ProductionGridFields.ServingDT];
        const day = moment(serveDate).format(DATE_FORMAT_DAY_OF_WEEK);

        return day;
      },
      Cell: (row) => <PopupTextCell text={row.value} />,
      ...ReactTableColumnProperties,
    },
    {
      Header: 'Serve Date',
      accessor: ProductionGridFields.ServingDT,
      Cell: DateCell,
      filterMethod: DateColumnFilter,
      ...ReactTableColumnProperties,
    },
    {
      id: ProductionGridFields.MealType,
      Header: 'Meal Type',
      accessor: (row) => {
        const mealType =
          MenuMealTypeKeysEnum[row[ProductionGridFields.MealType]];

        return mealType || '';
      },
      ...ReactTableColumnProperties,
    },
    {
      id: ProductionGridFields.Status,
      Header: 'Status',
      accessor: (row) => {
        const status =
          ProductionStatusKeyEnum[row[ProductionGridFields.Status]];

        return status || '';
      },
      Cell: (row) => <PopupTextCell text={row.value} />,
      ...ReactTableColumnProperties,
    },
  ];

  if (showCog) {
    columns.push({
      ...ReactTableWrapperCogColumnProperties,
      Cell: (gridProps) => GridContextMenu({ gridProps, options: menuOptions }),
    });
  }

  return columns;
};

export const createProdDetailKey = (key, fieldName) => `${key}-${fieldName}`;



// WEBPACK FOOTER //
// ./src/pages/manageproduction/helpers/productionRecord.js